if (!customElements.get('esalon-card-add')) {
  let activeClosePicker = null;

  customElements.define(
    'esalon-card-add',
    class EsalonCardAdd extends HTMLElement {
      connectedCallback() {
        const form = this.querySelector('form');
        if (!form) return;
        this._boundSubmit = this.onSubmit.bind(this);
        form.addEventListener('submit', this._boundSubmit);
      }

      disconnectedCallback() {
        const form = this.querySelector('form');
        if (form && this._boundSubmit) form.removeEventListener('submit', this._boundSubmit);
      }

      onSubmit(evt) {
        evt.preventDefault();
        evt.stopPropagation();

        const variantData = this.getVariantData();
        if (variantData && variantData.variants.length > 1) {
          this.showPicker(variantData);
          return;
        }

        const variantId = evt.target.querySelector('[name="id"]').value;
        this.addToCart(variantId);
      }

      getVariantData() {
        const card = this.closest('.esalon-card-product');
        if (!card) return null;
        const script = card.querySelector('.esalon-card-product__variant-data');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return null;
        }
      }

      showPicker(variantData) {
        const card = this.closest('.esalon-card-product');
        if (!card) return;

        // Close any existing picker through its own routine so its document listener is removed
        if (activeClosePicker) activeClosePicker();

        const firstAvailable = variantData.variants.find((v) => v.available) ?? variantData.variants[0];
        const selectedOptions = [...firstAvailable.options];
        const picker = this.buildPicker(variantData, selectedOptions);

        const closePicker = () => {
          picker.remove();
          card.classList.remove('esalon-card-product--picker-open');
          document.removeEventListener('click', handleOutsideClick);
          activeClosePicker = null;
        };

        const handleOutsideClick = (evt) => {
          if (!picker.contains(evt.target)) closePicker();
        };

        const updateAddBtn = () => {
          const addBtn = picker.querySelector('.esalon-card-variant-picker__add-btn');
          const variant = variantData.variants.find((v) => selectedOptions.every((opt, i) => v.options[i] === opt));
          const soldOut = !variant || !variant.available;
          addBtn.textContent = soldOut ? window.variantStrings.soldOut : window.variantStrings.addToCart;
          addBtn.disabled = soldOut;
        };

        picker.querySelector('.esalon-card-variant-picker__close').addEventListener('click', closePicker);

        picker.querySelectorAll('.esalon-card-variant-picker__option-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = parseInt(btn.dataset.optionIndex, 10);
            selectedOptions[idx] = btn.dataset.value;
            picker
              .querySelectorAll(`.esalon-card-variant-picker__option-btn[data-option-index="${idx}"]`)
              .forEach((b) => {
                const active = b === btn;
                b.classList.toggle('is-selected', active);
                b.setAttribute('aria-pressed', String(active));
              });
            updateAddBtn();
          });
        });

        picker.querySelector('.esalon-card-variant-picker__add-btn').addEventListener('click', () => {
          const variant = variantData.variants.find((v) =>
            selectedOptions.every((opt, i) => v.options[i] === opt)
          );
          if (!variant) return;
          closePicker();
          this.addToCart(String(variant.id));
        });

        // Center over the product image before appending so there's no layout flash
        const media = card.querySelector('.esalon-card-product__media');
        if (media) {
          const cardRect = card.getBoundingClientRect();
          const mediaRect = media.getBoundingClientRect();
          picker.style.left = `${mediaRect.left - cardRect.left + mediaRect.width / 2}px`;
          picker.style.top = `${mediaRect.top - cardRect.top + mediaRect.height / 2}px`;
          picker.style.transform = 'translate(-50%, -50%)';
        }

        activeClosePicker = closePicker;
        card.classList.add('esalon-card-product--picker-open');
        card.appendChild(picker);
        // Defer so the click that opened the picker doesn't immediately close it
        setTimeout(() => document.addEventListener('click', handleOutsideClick), 0);
        picker.querySelector('.esalon-card-variant-picker__option-btn')?.focus();
      }

      buildPicker(variantData, selectedOptions) {
        const picker = document.createElement('div');
        picker.className = 'esalon-card-variant-picker';
        picker.setAttribute('role', 'dialog');
        picker.setAttribute('aria-label', 'Select a variant');

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'esalon-card-variant-picker__close';
        closeBtn.setAttribute('aria-label', 'Close');
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '14');
        svg.setAttribute('height', '14');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '2');
        svg.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M18 6L6 18M6 6l12 12');
        svg.appendChild(path);
        closeBtn.appendChild(svg);
        picker.appendChild(closeBtn);

        variantData.options.forEach((optName, idx) => {
          const values = [...new Set(variantData.variants.map((v) => v.options[idx]))];

          const group = document.createElement('div');
          group.className = 'esalon-card-variant-picker__group';

          const label = document.createElement('p');
          label.className = 'esalon-card-variant-picker__option-name';
          label.textContent = optName;
          group.appendChild(label);

          const optionsEl = document.createElement('div');
          optionsEl.className = 'esalon-card-variant-picker__options';

          values.forEach((val) => {
            const isSelected = val === selectedOptions[idx];
            const isSoldOut = !variantData.variants.some((v) => v.options[idx] === val && v.available);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'esalon-card-variant-picker__option-btn';
            if (isSelected) btn.classList.add('is-selected');
            if (isSoldOut) btn.classList.add('is-sold-out');
            btn.dataset.optionIndex = String(idx);
            btn.dataset.value = val;
            btn.setAttribute('aria-pressed', String(isSelected));
            btn.textContent = val;
            optionsEl.appendChild(btn);
          });

          group.appendChild(optionsEl);
          picker.appendChild(group);
        });

        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'esalon-card-variant-picker__add-btn';
        const initialVariant = variantData.variants.find((v) => selectedOptions.every((opt, i) => v.options[i] === opt));
        const initialSoldOut = !initialVariant || !initialVariant.available;
        addBtn.textContent = initialSoldOut ? window.variantStrings.soldOut : window.variantStrings.addToCart;
        addBtn.disabled = initialSoldOut;
        picker.appendChild(addBtn);

        return picker;
      }

      addToCart(variantId) {
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (!cart) {
          const form = this.querySelector('form');
          if (form) {
            const idInput = form.querySelector('[name="id"]');
            if (idInput) idInput.value = variantId;
            form.submit();
          }
          return;
        }

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
        formData.append('sections_url', window.location.pathname);

        fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        })
          .then((res) => res.json())
          .then((response) => {
            if (response.status) {
              console.error('[esalon-card-add] 422 from cart/add:', response.description || response.message, response);
              return;
            }
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch(console.error);
      }
    }
  );
}
